import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Trophy, Target, Award, Calendar, Plus, Upload, MessageSquare, Trash2, Edit, CheckCircle, Settings, Users, CheckCircle2, AlertTriangle, XCircle, Minus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Colour-blind-safe status (White/Yellow/Blue/Red only, never colour alone —
// each carries a text label + a distinct icon/shape).
function CurrentStatusBadge({ st, currentLabel }) {
  const term = currentLabel || "term";
  if (!st) return null;
  if (st.current_status === "paid") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white px-2.5 py-1 text-xs font-bold border border-blue-300">
        <CheckCircle2 className="w-3.5 h-3.5" />{term} · PAID
      </span>
    );
  }
  if (st.current_status === "unpaid") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-yellow-400 text-black px-2.5 py-1 text-xs font-bold border border-yellow-600">
        <AlertTriangle className="w-3.5 h-3.5" />{term} · UNPAID
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white px-2.5 py-1 text-xs font-bold border border-red-300">
      <XCircle className="w-3.5 h-3.5" />NOT IN {term}
      {st.last_registered_label && <span className="ml-1 font-normal opacity-90">· Last: {st.last_registered_label}</span>}
    </span>
  );
}

function TermChip({ t }) {
  if (t.paid) {
    return <span className="inline-flex items-center gap-1 rounded bg-blue-600 text-white border border-blue-300 px-2 py-0.5 text-[11px] font-bold"><CheckCircle2 className="w-3 h-3" />{t.label}</span>;
  }
  if (t.registered) {
    return <span className="inline-flex items-center gap-1 rounded bg-yellow-400 text-black border border-yellow-600 px-2 py-0.5 text-[11px] font-bold"><AlertTriangle className="w-3 h-3" />{t.label}</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded border border-slate-500 text-slate-400 px-2 py-0.5 text-[11px] font-medium"><Minus className="w-3 h-3" />{t.label}</span>;
}

export default function AcademyTeamDetails({ user }) {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Display-only registration/payment status (per player). Read-only.
  const [rosterStatus, setRosterStatus] = useState({}); // player_id -> status obj
  const [rosterMeta, setRosterMeta] = useState(null);   // { summary, current_term }
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Add player dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addMode, setAddMode] = useState("existing"); // "existing" or "guest"
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedExistingPlayer, setSelectedExistingPlayer] = useState(null);
  
  // Guest player fields
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  
  const [adding, setAdding] = useState(false);
  
  // Bulk upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Edit player dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editGivenName, setEditGivenName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editBirthdate, setEditBirthdate] = useState("");
  const [editPlayingPosition, setEditPlayingPosition] = useState("");
  const [updating, setUpdating] = useState(false);

  const isCoach = user?.roles?.includes("academy_coach") && user?.academy_coach_teams?.includes(teamId);
  const isAdmin = ["convenor", "captain"].includes(user?.role);
  const isParent = user?.roles?.includes("academy_parent");
  const canManage = isCoach || isAdmin;

  useEffect(() => {
    fetchTeamDetails();
    fetchRosterStatus();
  }, [teamId]);

  // Display-only: fetch per-player registration + payment status for admins/coaches.
  const fetchRosterStatus = async () => {
    if (!(isCoach || isAdmin)) return;
    try {
      const res = await axios.get(`${API}/academy/admin/team-roster/${teamId}`, {
        withCredentials: true
      });
      const map = {};
      (res.data?.players || []).forEach((p) => { map[p.id] = p; });
      setRosterStatus(map);
      setRosterMeta({ summary: res.data?.summary, current_term: res.data?.current_term, term_labels: res.data?.term_labels || [] });
    } catch (error) {
      // Non-blocking: page still works without status overlay.
      console.error("Error fetching roster status:", error);
    }
  };

  const fetchTeamDetails = async () => {
    try {
      // If parent, check if they have children in this team
      if (isParent && !isAdmin) {
        const childrenResponse = await axios.get(`${API}/academy/my-children`, {
          withCredentials: true
        });
        
        // Check if any child is in this team
        const hasChildInTeam = childrenResponse.data.some(child => child.team_id === teamId);
        
        if (!hasChildInTeam) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
      }
      
      const response = await axios.get(`${API}/academy/teams/${teamId}`, {
        withCredentials: true
      });
      setTeam(response.data);
    } catch (error) {
      console.error("Error fetching team details:", error);
      toast.error("Failed to load team details");
    } finally {
      setLoading(false);
    }
  };

  const resetAddForm = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedExistingPlayer(null);
    setGuestName("");
    setGuestEmail("");
    setGuestPhone("");
    setAddMode("existing");
  };

  const searchPlayers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `${API}/academy/players/search?q=${encodeURIComponent(query)}`,
        { withCredentials: true }
      );
      setSearchResults(response.data);
    } catch (error) {
      console.error("Error searching players:", error);
      toast.error("Failed to search players");
    } finally {
      setSearching(false);
    }
  };

  const addExistingPlayer = async () => {
    if (!selectedExistingPlayer) {
      toast.error("Please select a player");
      return;
    }

    setAdding(true);
    try {
      await axios.post(
        `${API}/academy/teams/${teamId}/add-existing-player`,
        { player_id: selectedExistingPlayer.id },
        { withCredentials: true }
      );

      toast.success(`${selectedExistingPlayer.child_name} added to team!`);
      setShowAddDialog(false);
      resetAddForm();
      fetchTeamDetails();
    } catch (error) {
      console.error("Error adding player:", error);
      toast.error(error.response?.data?.detail || "Failed to add player");
    } finally {
      setAdding(false);
    }
  };

  const addGuestPlayer = async () => {
    if (!guestName || !guestEmail) {
      toast.error("Please provide guest name and email");
      return;
    }

    setAdding(true);
    try {
      await axios.post(
        `${API}/academy/teams/${teamId}/add-guest-player`,
        {
          name: guestName,
          email: guestEmail,
          phone: guestPhone || null
        },
        { withCredentials: true }
      );

      toast.success("Guest player added to team!");
      setShowAddDialog(false);
      resetAddForm();
      fetchTeamDetails();
    } catch (error) {
      console.error("Error adding guest:", error);
      toast.error(error.response?.data?.detail || "Failed to add guest player");
    } finally {
      setAdding(false);
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API}/academy/teams/bulk-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true
        }
      );

      setUploadResults(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully added ${response.data.success} players!`);
        fetchTeamDetails();
      }
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} players failed to add`);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `Given Name,Last Name,Date of Birth,Playing Position,Membership Number,Team ID\nJohnny,Tan,2015-03-15,Forward,15T0001,${teamId}\nJamie,Wong,2014-07-20,Midfielder,14W0001,${teamId}`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "academy_bulk_add_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  const removePlayer = async (playerId, playerName) => {
    if (!window.confirm(`Are you sure you want to remove ${playerName} from this team?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/academy/teams/${teamId}/remove-player/${playerId}`,
        { withCredentials: true }
      );

      toast.success(`${playerName} removed from team`);
      fetchTeamDetails();
    } catch (error) {
      console.error("Error removing player:", error);
      toast.error(error.response?.data?.detail || "Failed to remove player");
    }
  };

  const openEditDialog = (player) => {
    setEditingPlayer(player);
    setEditGivenName(player.given_name || "");
    setEditLastName(player.last_name || "");
    setEditBirthdate(player.birthdate || "");
    setEditPlayingPosition(player.playing_position || "");
    setShowEditDialog(true);
  };

  const updatePlayer = async () => {
    if (!editGivenName.trim() || !editLastName.trim() || !editBirthdate) {
      toast.error("Please fill in given name, last name, and birthdate");
      return;
    }

    setUpdating(true);
    try {
      await axios.put(
        `${API}/academy/players/${editingPlayer.id}`,
        {
          given_name: editGivenName,
          last_name: editLastName,
          birthdate: editBirthdate,
          playing_position: editPlayingPosition || null
        },
        { withCredentials: true }
      );

      toast.success("Player updated successfully!");
      setShowEditDialog(false);
      setEditingPlayer(null);
      fetchTeamDetails();
    } catch (error) {
      console.error("Error updating player:", error);
      toast.error(error.response?.data?.detail || "Failed to update player");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#0a0f1a]">
        <header className="bg-slate-950 border-b border-white/5 shadow-lg">
          <div className="container mx-auto px-4 py-4">
            <Link to="/academy/teams">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Teams
              </Button>
            </Link>
          </div>
        </header>
        <div className="container mx-auto px-4 py-12">
          <Card className="p-8 text-center max-w-md mx-auto">
            <div className="text-6xl mb-4">🔒</div>
            <h3 className="text-xl font-bold text-white mb-2">Access Restricted</h3>
            <p className="text-slate-400 mb-4">
              You can only view teams that your children are part of.
            </p>
            <Link to="/academy/teams">
              <Button className="bg-blue-600 hover:bg-blue-700">
                View My Kids' Teams
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8">
          <p className="text-slate-400">Team not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/academy/teams">
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Teams
            </Button>
          </Link>
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white">{team.name}</h1>
            {isAdmin && (
              <Link to={`/academy/teams/${teamId}/settings`}>
                <Button variant="ghost" size="sm" className="text-white hover:text-blue-200">
                  <Settings className="w-4 h-4 mr-1" />
                  Settings
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Team Info Card */}
        <div className="mb-8">
          <Card className="p-6 bg-slate-900/50 border-2 border-blue-500/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-400">Age Group</p>
                <p className="text-xl font-bold text-white">{team.age_group}</p>
              </div>
              <div>
                <p className="text-sm text-slate-400">Gender</p>
                <p className="text-xl font-bold text-white capitalize">{team.gender}</p>
              </div>
              <div>
                <p className="text-sm text-slate-400">Session Type</p>
                <p className="text-xl font-bold text-white capitalize">{team.session_type}</p>
              </div>
            </div>

            {team.coach && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-slate-400">Head Coach</p>
                <p className="text-lg font-semibold text-white">{team.coach.name}</p>
              </div>
            )}
          </Card>

          {/* Action Buttons for Coaches/Admins */}
          {canManage && (
            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Player
              </Button>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
              >
                <Upload className="w-5 h-5 mr-2" />
                Bulk Upload
              </Button>
              <Link to={`/academy/sessions?team_id=${teamId}`}>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Calendar className="w-4 h-4 mr-2" />
                  View Training Sessions
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Players Roster */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-white mb-4">
            Players ({team.players?.length || 0})
          </h3>

          {/* Display-only registration + payment summary (admins/coaches) */}
          {rosterMeta?.summary && (isAdmin || isCoach) && (
            <>
              <Card className="p-3 mb-3 bg-slate-900/60 border border-blue-500/30" data-testid="players-status-summary">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-bold text-white">{rosterMeta.summary.total} in bucket</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-blue-300 font-semibold">{rosterMeta.summary.registered_current} registered {rosterMeta.current_term?.label || "term"}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-blue-300 font-semibold inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />{rosterMeta.summary.paid_current} paid
                  </span>
                  <span className="text-slate-500">·</span>
                  <span className="text-red-300 font-semibold inline-flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" />{rosterMeta.summary.not_registered_current} not in {rosterMeta.current_term?.label || "term"}
                  </span>
                </div>
              </Card>

              {/* Filter bar */}
              <div className="flex flex-wrap gap-2 mb-4" data-testid="players-status-filters">
                {[
                  { key: "all", label: `All (${rosterMeta.summary.total})` },
                  { key: "registered", label: `Registered ${rosterMeta.current_term?.label || "term"} (${rosterMeta.summary.registered_current})` },
                  { key: "paid", label: `Paid ${rosterMeta.current_term?.label || "term"} (${rosterMeta.summary.paid_current})` },
                  { key: "not_registered", label: `Not registered ${rosterMeta.current_term?.label || "term"} (${rosterMeta.summary.not_registered_current})` },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    data-testid={`players-filter-${f.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      statusFilter === f.key
                        ? "bg-blue-500/30 text-blue-100 border-blue-400"
                        : "bg-slate-800/40 text-slate-300 border-slate-700 hover:border-slate-500"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {!team.players || team.players.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-slate-400">No players in this team yet.</p>
              {canManage && (
                <p className="text-sm text-slate-400 mt-2">Use the "Add Player" or "Bulk Upload" buttons above to add players.</p>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {team.players.filter((player) => {
                if (statusFilter === "all") return true;
                const cs = rosterStatus[player.id]?.current_status;
                if (statusFilter === "registered") return cs === "paid" || cs === "unpaid";
                if (statusFilter === "paid") return cs === "paid";
                if (statusFilter === "not_registered") return cs === "not_registered" || !cs;
                return true;
              }).map((player) => (
                <Card key={player.id} className="p-4 bg-slate-900/50 border border-blue-500/30 hover:border-blue-500/30 transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-lg font-bold text-white">{player.child_name}</h4>
                      {player.membership_number && (
                        <p className="text-xs text-slate-400">{player.membership_number}</p>
                      )}
                      {player.played_up && player.true_age_group && (
                        <span
                          className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          data-testid={`player-played-up-${player.id}`}
                          title={`This child's true age group is ${player.true_age_group}. No ${player.true_age_group} bucket exists, so they are playing up in ${player.assigned_bucket_age_group || team.age_group}.`}
                        >
                          {player.true_age_group} (playing up in {player.assigned_bucket_age_group || team.age_group} — no {player.true_age_group} bucket)
                        </span>
                      )}
                    </div>
                    {(isAdmin || isCoach) && rosterMeta && (
                      <div data-testid={`player-current-status-${player.id}`}>
                        <CurrentStatusBadge
                          st={rosterStatus[player.id] || { current_status: "not_registered", last_registered_label: null }}
                          currentLabel={rosterMeta?.current_term?.label}
                        />
                      </div>
                    )}
                  </div>

                  {(isAdmin || isCoach) && rosterMeta && (
                    <div className="mb-3" data-testid={`player-status-block-${player.id}`}>
                      {(rosterStatus[player.id]?.joined_label) && (
                        <p className="text-xs text-slate-400 mb-1.5">Joined: {rosterStatus[player.id].joined_label}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5" data-testid={`player-terms-${player.id}`}>
                        {(rosterStatus[player.id]?.terms
                          || (rosterMeta.term_labels || []).map((l) => ({ label: l, registered: false, paid: false }))
                        ).map((t) => (
                          <TermChip key={t.label} t={t} />
                        ))}
                      </div>
                    </div>
                  )}

                  {player.playing_position && (
                    <div className="mb-3">
                      <p className="text-xs text-slate-400">Position</p>
                      <p className="text-sm font-semibold text-white">{player.playing_position}</p>
                    </div>
                  )}

                  {/* Stats - Respect Privacy Settings */}
                  {(isAdmin || isCoach || team?.show_individual_stats) ? (
                    // Full stats visible
                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div className="p-2 bg-emerald-500/10 rounded">
                        <Trophy className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-300">{player.goals_scored || 0}</p>
                        <p className="text-xs text-slate-400">Goals</p>
                      </div>
                      <div className="p-2 bg-blue-500/10 rounded">
                        <Target className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-blue-300">{player.assists || 0}</p>
                        <p className="text-xs text-slate-400">Assists</p>
                      </div>
                      <div className="p-2 bg-yellow-500/10 rounded">
                        <Award className="w-4 h-4 text-yellow-500 mx-auto mb-1" />
                        <p className="text-lg font-bold text-yellow-300">{player.motm_awards || 0}</p>
                        <p className="text-xs text-slate-400">MOTM</p>
                      </div>
                    </div>
                  ) : (
                    // Privacy mode: Show only Games Played, hide individual stats
                    <div className="mb-3">
                      <div className="p-2 bg-purple-500/10 rounded text-center mb-2">
                        <Calendar className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-purple-300">{player.games_played || 0}</p>
                        <p className="text-xs text-slate-400">Games Played</p>
                      </div>
                      <div className="p-2 bg-blue-500/10 rounded text-center">
                        <p className="text-xs text-blue-300">
                          🔒 <strong>Individual stats are private</strong>
                        </p>
                        <p className="text-xs text-blue-400 mt-1">
                          Hidden by team settings
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Age */}
                  {player.birthdate && (
                    <div className="mb-3 text-center">
                      <p className="text-xs text-slate-400">
                        Age: {new Date().getFullYear() - new Date(player.birthdate).getFullYear()} years
                      </p>
                    </div>
                  )}

                  {/* Actions for Coaches */}
                  {canManage && (
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t">
                      <Link to={`/academy/player/${player.id}/feedback`}>
                        <Button size="sm" variant="outline" className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => openEditDialog(player)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={() => removePlayer(player.id, player.child_name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Player Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetAddForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Player to Team</DialogTitle>
          </DialogHeader>
          
          {/* Mode Selector */}
          <div className="flex gap-2 border-b pb-4">
            <Button
              onClick={() => setAddMode("existing")}
              variant={addMode === "existing" ? "default" : "outline"}
              className={addMode === "existing" ? "bg-blue-600" : ""}
            >
              Add Existing Player
            </Button>
            <Button
              onClick={() => setAddMode("guest")}
              variant={addMode === "guest" ? "default" : "outline"}
              className={addMode === "guest" ? "bg-purple-600" : ""}
            >
              Add Guest Player
            </Button>
          </div>

          <div className="space-y-4 mt-4">
            {addMode === "existing" ? (
              <>
                {/* Search Existing Players */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Search Registered Players</label>
                  <Input
                    placeholder="Search by player name or membership number..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchPlayers(e.target.value);
                    }}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Type at least 2 characters to search
                  </p>
                </div>

                {/* Search Results */}
                {searching && <p className="text-sm text-slate-400">Searching...</p>}
                
                {searchResults.length > 0 && (
                  <div className="border rounded-md max-h-60 overflow-y-auto">
                    {searchResults.map((player) => (
                      <div
                        key={player.id}
                        onClick={() => setSelectedExistingPlayer(player)}
                        className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-blue-500/10 ${
                          selectedExistingPlayer?.id === player.id ? 'bg-blue-500/20' : ''
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{player.child_name}</p>
                            <p className="text-sm text-slate-400">{player.membership_number}</p>
                            {player.current_team && (
                              <p className="text-xs text-slate-400">Currently in: {player.current_team}</p>
                            )}
                          </div>
                          {selectedExistingPlayer?.id === player.id && (
                            <CheckCircle className="w-5 h-5 text-blue-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No players found</p>
                )}

                {selectedExistingPlayer && (
                  <div className="p-3 bg-emerald-500/10 border border-green-500/30 rounded">
                    <p className="text-sm text-emerald-400">
                      ✓ Selected: <strong>{selectedExistingPlayer.child_name}</strong> ({selectedExistingPlayer.membership_number})
                    </p>
                  </div>
                )}

                <Button
                  onClick={addExistingPlayer}
                  disabled={adding || !selectedExistingPlayer}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {adding ? "Adding..." : "Add to Team"}
                </Button>
              </>
            ) : (
              <>
                {/* Guest Player Form */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Guest Name *</label>
                    <Input
                      placeholder="Guest Player Name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Contact Email *</label>
                    <Input
                      type="email"
                      placeholder="guest@example.com"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Contact Phone</label>
                    <Input
                      placeholder="+65 9123 4567"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                    />
                  </div>
                </div>

                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded">
                  <p className="text-xs text-purple-400">
                    <strong>Guest Players:</strong> Temporary players without membership. They can be added to specific training sessions or games.
                  </p>
                </div>

                <Button
                  onClick={addGuestPlayer}
                  disabled={adding}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {adding ? "Adding..." : "Add Guest Player"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload Players</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Upload CSV or Excel File</label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
              />
              <p className="text-xs text-slate-400 mt-1">
                Required columns: Player Name, Date of Birth, Membership Number, Team ID<br/>
                Optional: Playing Position
              </p>
            </div>

            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="w-full"
            >
              Download CSV Template
            </Button>

            {uploadResults && (
              <Card className="p-4 bg-[#0a0f1a]">
                <h4 className="font-semibold mb-2">Upload Results:</h4>
                <p className="text-sm text-emerald-400">✓ Success: {uploadResults.success}</p>
                <p className="text-sm text-red-400">✗ Failed: {uploadResults.failed}</p>
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-slate-300 mb-1">Errors:</p>
                    <ul className="text-xs text-slate-400 space-y-1">
                      {uploadResults.errors.map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}

            <Button
              onClick={handleFileUpload}
              disabled={uploading || !file}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
            >
              {uploading ? "Uploading..." : "Upload Players"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Player Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Player</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-2 block">Given Name *</label>
                <Input
                  placeholder="Johnny"
                  value={editGivenName}
                  onChange={(e) => setEditGivenName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Last Name *</label>
                <Input
                  placeholder="Tan"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Birthdate *</label>
              <Input
                type="date"
                value={editBirthdate}
                onChange={(e) => setEditBirthdate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Playing Position</label>
              <Input
                placeholder="e.g., Forward, Midfielder, Defender, Goalkeeper"
                value={editPlayingPosition}
                onChange={(e) => setEditPlayingPosition(e.target.value)}
              />
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
              <p className="text-xs text-blue-400">
                <strong>Note:</strong> Parent information cannot be edited here. Membership number remains unchanged.
              </p>
            </div>

            <Button
              onClick={updatePlayer}
              disabled={updating}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
            >
              {updating ? "Updating..." : "Update Player"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
